import { useState, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { Upload, Download, Trash2, X, FileText } from 'lucide-react'

import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { ApiResponse } from '@/types/api'
import type { Document, DocumentType } from '@/types/models'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FILTER_TABS: { key: DocumentType | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'ESSAY', label: 'Essay' },
  { key: 'RESUME', label: 'Resume' },
  { key: 'TRANSCRIPT', label: 'Transcript' },
  { key: 'TEST_SCORE', label: 'Test Score' },
  { key: 'RECOMMENDATION_LETTER', label: 'LOR' },
  { key: 'OTHER', label: 'Other' },
]

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB

const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
]

const TYPE_BADGE: Record<DocumentType, string> = {
  ESSAY: 'bg-blue-100 text-blue-700',
  RESUME: 'bg-purple-100 text-purple-700',
  TRANSCRIPT: 'bg-yellow-100 text-yellow-700',
  TEST_SCORE: 'bg-orange-100 text-orange-700',
  RECOMMENDATION_LETTER: 'bg-green-100 text-green-700',
  OTHER: 'bg-muted text-muted-foreground',
}

const TYPE_LABEL: Partial<Record<DocumentType, string>> = {
  RECOMMENDATION_LETTER: 'LOR',
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ---------------------------------------------------------------------------
// UploadZone
// ---------------------------------------------------------------------------

const UPLOAD_TYPE_OPTIONS: { value: DocumentType; label: string }[] = [
  { value: 'ESSAY', label: 'Essay' },
  { value: 'RESUME', label: 'Resume' },
  { value: 'TRANSCRIPT', label: 'Transcript' },
  { value: 'TEST_SCORE', label: 'Test Score' },
  { value: 'RECOMMENDATION_LETTER', label: 'LOR' },
  { value: 'OTHER', label: 'Other' },
]

interface UploadZoneProps {
  onUpload: (file: File) => void
  uploading: boolean
  progress: number
  selectedType: DocumentType
  onTypeChange: (type: DocumentType) => void
}

function UploadZone({ onUpload, uploading, progress, selectedType, onTypeChange }: UploadZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [fileError, setFileError] = useState('')

  function validateAndUpload(file: File) {
    setFileError('')
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      setFileError('Only PDF, DOCX, and TXT files are accepted.')
      return
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError('File must be smaller than 10 MB.')
      return
    }
    onUpload(file)
  }

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files[0]
      if (file) validateAndUpload(file)
    },
    [onUpload],
  )

  return (
    <div
      className={cn(
        'relative rounded-xl border-2 border-dashed p-8 text-center transition-colors',
        dragOver ? 'border-brand-blue bg-brand-blue/5' : 'border-muted-foreground/30',
        uploading && 'pointer-events-none opacity-60',
      )}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) validateAndUpload(file)
          e.target.value = ''
        }}
        aria-label="Upload document"
      />
      <Upload className="mx-auto h-8 w-8 text-muted-foreground" />
      <p className="mt-2 text-sm font-medium text-foreground">
        Drag and drop a file here, or{' '}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="text-brand-blue hover:underline focus:outline-none"
        >
          click to browse
        </button>
      </p>
      <p className="mt-1 text-xs text-muted-foreground">PDF, DOCX, TXT — max 10 MB</p>

      <div className="mt-4 flex items-center justify-center gap-2">
        <label htmlFor="doc-type-select" className="text-xs text-muted-foreground whitespace-nowrap">
          Document type:
        </label>
        <select
          id="doc-type-select"
          value={selectedType}
          onChange={(e) => onTypeChange(e.target.value as DocumentType)}
          className="rounded border border-input bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-brand-blue"
        >
          {UPLOAD_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {uploading && (
        <div className="mt-4">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-brand-blue transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{progress}% uploaded</p>
        </div>
      )}

      {fileError && (
        <p className="mt-2 text-xs text-destructive">{fileError}</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// DocumentsPage
// ---------------------------------------------------------------------------

/**
 * DocumentsPage — document library with upload zone and type filters.
 *
 * Supports PDF, DOCX, TXT up to 10 MB.
 * Download opens a pre-signed URL from GET /documents/:id/url.
 */
export default function DocumentsPage() {
  const queryClient = useQueryClient()
  const { currentWorkspaceId } = useWorkspaceStore()
  const [activeFilter, setActiveFilter] = useState<DocumentType | 'ALL'>('ALL')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadType, setUploadType] = useState<DocumentType>('OTHER')
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null)

  // Fetch documents
  const { data, isLoading } = useQuery<ApiResponse<Document[]>>({
    queryKey: ['documents', currentWorkspaceId],
    queryFn: () => api.get<ApiResponse<Document[]>>('/v1/documents'),
    enabled: !!currentWorkspaceId,
  })
  const allDocuments = data?.data ?? []
  const documents =
    activeFilter === 'ALL'
      ? allDocuments
      : allDocuments.filter((d) => d.type === activeFilter)

  // Upload via api.upload (handles auth headers automatically)
  function handleUpload(file: File) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', uploadType)

    ;(async () => {
      setUploading(true)
      setUploadProgress(0)
      try {
        await api.upload('/v1/documents/upload', formData)
        queryClient.invalidateQueries({ queryKey: ['documents', currentWorkspaceId] })
      } finally {
        setUploading(false)
        setUploadProgress(0)
      }
    })()
  }

  // Download
  const downloadMutation = useMutation({
    mutationFn: async (doc: Document) => {
      const res = await api.get<ApiResponse<{ url: string }>>(`/v1/documents/${doc.id}/url`)
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer')
      }
    },
  })

  // Delete
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/documents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', currentWorkspaceId] })
      setDeleteTarget(null)
    },
  })

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-semibold text-foreground mb-6">Documents</h1>

      {/* Upload zone */}
      <UploadZone
        onUpload={handleUpload}
        uploading={uploading}
        progress={uploadProgress}
        selectedType={uploadType}
        onTypeChange={setUploadType}
      />

      {/* Filter tabs */}
      <div className="mt-6 flex gap-1 border-b">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveFilter(tab.key)}
            className={cn(
              'px-4 py-2 text-sm font-medium transition-colors focus:outline-none',
              activeFilter === tab.key
                ? 'border-b-2 border-brand-blue text-brand-blue'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Document list */}
      <div className="mt-4">
        {isLoading && (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading...</div>
        )}

        {!isLoading && documents.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No documents found.
          </div>
        )}

        {documents.length > 0 && (
          <div className="rounded-xl border overflow-hidden">
            {documents.map((doc, idx) => (
              <div
                key={doc.id}
                className={cn(
                  'flex items-center gap-4 px-4 py-3',
                  idx !== documents.length - 1 && 'border-b',
                )}
              >
                <FileText className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">{doc.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatBytes(doc.fileSize)} &middot;{' '}
                    {new Date(doc.uploadedAt ?? doc.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <span
                  className={cn(
                    'hidden sm:inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                    TYPE_BADGE[doc.type] ?? TYPE_BADGE.OTHER,
                  )}
                >
                  {TYPE_LABEL[doc.type] ?? doc.type}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => downloadMutation.mutate(doc)}
                    disabled={downloadMutation.isPending}
                    title="Download"
                    className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
                    aria-label="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(doc)}
                    title="Delete"
                    className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive"
                    aria-label="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <Dialog.Root open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-background p-6 shadow-xl focus:outline-none">
            <div className="flex items-start justify-between mb-3">
              <Dialog.Title className="text-base font-semibold text-foreground">
                Delete Document
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded p-1 text-muted-foreground hover:bg-accent"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </Dialog.Close>
            </div>
            <Dialog.Description className="text-sm text-muted-foreground">
              Are you sure you want to delete{' '}
              <strong>{deleteTarget?.name}</strong>? This cannot be undone.
            </Dialog.Description>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="flex-1 rounded-md border px-4 py-2 text-sm hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
                className="flex-1 rounded-md bg-destructive px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
